import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ErrorAlert } from '@/components/ErrorAlert';
import { EventList } from '@/components/EventList';
import { PageHeader } from '@/components/PageHeader';
import { client } from '@/lib/orpc';
import { useMe } from '@/lib/session';

const categories = ['ticket', 'account', 'admin', 'security', 'activity'] as const;

export const Route = createFileRoute('/_app/log')({
  validateSearch: z.object({ category: z.enum(categories).optional() }),
  component: LogPage
});

function LogPage() {
  const { t } = useTranslation();
  const me = useMe();
  const { category } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const log = useInfiniteQuery({
    queryKey: ['events', category],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => client.events.list({ limit: 50, ...(category && { categories: [category] }), ...(pageParam && { before: pageParam }) }),
    getNextPageParam: (last) => last.nextBefore ?? undefined
  });
  return (
    <div>
      <PageHeader title={t('log.title')} lead={t('log.lead')}>
        <select
          className="select select-sm w-auto"
          aria-label={t('log.category')}
          value={category ?? ''}
          onChange={(e) => void navigate({ search: { category: (e.target.value || undefined) as (typeof categories)[number] | undefined } })}
        >
          <option value="">{t('common.all')}</option>
          {categories
            .filter((c) => (c !== 'security' && c !== 'activity') || me.role === 'admin')
            .map((c) => (
              <option key={c} value={c}>
                {t(`eventCategory.${c}`)}
              </option>
            ))}
        </select>
      </PageHeader>
      {log.isError && <ErrorAlert error={log.error} />}
      {log.data && <EventList items={log.data.pages.flatMap((p) => p.items)} />}
      {log.hasNextPage && (
        <button type="button" className="btn btn-sm mt-4" onClick={() => void log.fetchNextPage()}>
          {t('common.loadMore')}
        </button>
      )}
    </div>
  );
}
